/**
 * Lógica de acceso a rutas del dashboard.
 *
 * `ProtectedRoute` (src/core/RouteGuards.jsx) decide si un usuario puede
 * entrar a una ruta. Este módulo expone esa misma decisión como función pura
 * para que los componentes que muestran accesos (filas de tablas, botones,
 * enlaces) consulten la misma fuente de verdad y no ofrezcan accesos que el
 * guard va a rechazar.
 */

/**
 * Resuelve si un usuario puede acceder a una ruta del dashboard.
 *
 * Reglas (idénticas a las de `ProtectedRoute`):
 * - El superusuario tiene acceso total.
 * - Si el permiso `dashboard:view:<path>` existe en el catálogo, el acceso
 *   depende de que el usuario lo tenga asignado.
 * - Si el permiso NO existe en el catálogo, el acceso depende de los roles
 *   contemplados en `ROUTE_ALLOWED_ROLES` para esa ruta.
 *
 * @param {object} params
 * @param {string} params.path Ruta a evaluar (ej. '/baremos/imagenmed').
 * @param {string[]} [params.allowedRoles] Roles con acceso por defecto a la ruta.
 * @param {string|null} [params.role] Rol del usuario.
 * @param {boolean} [params.isAdmin] El usuario es superusuario.
 * @param {boolean} [params.isAuthenticated] Hay sesión iniciada.
 * @param {boolean} [params.permissionExists] Si `dashboard:view:<path>` está en el catálogo.
 * @param {string[]} [params.userPermissions] Permisos efectivos del usuario.
 * @returns {boolean} `true` si el usuario puede entrar a la ruta.
 */
export function resolveRouteAccess({
  path,
  allowedRoles = [],
  role,
  isAdmin,
  isAuthenticated,
  permissionExists,
  userPermissions = [],
}) {
  if (isAdmin) return true;
  if (!isAuthenticated) return false;

  if (permissionExists) {
    return userPermissions.includes(`dashboard:view:${path}`);
  }

  return allowedRoles.includes(role);
}
